import type {
  AttackPattern,
  BattleOutcome,
  Enemy,
  Hero,
  Weapon,
} from '../../types';
import {
  REP_DEATH,
  REP_HALL_OF_FAME,
  REP_VICTORY,
  REP_WEAPON_BROKE,
} from './Reputation';

/** Was the player's response correct for this attack pattern? */
export function isDefenseCorrect(
  pattern: AttackPattern,
  response: 'tap' | 'hold' | 'none',
): boolean {
  switch (pattern.kind) {
    case 'quick':
      return response === 'tap';
    case 'heavy':
      return response === 'hold';
    case 'feint':
      return response === 'none';
  }
}

/** Computes how much durability and HP the player loses on a defense outcome. */
export function resolveDefense(
  pattern: AttackPattern,
  correct: boolean,
): { hpLoss: number; durabilityLoss: number; fatigueGain: number } {
  if (correct) {
    return {
      hpLoss: 0,
      durabilityLoss: pattern.durabilityCorrect,
      fatigueGain: 1,
    };
  }
  // Feint tapped: heavy durability cost, no HP damage.
  if (pattern.kind === 'feint') {
    return { hpLoss: 0, durabilityLoss: pattern.durabilityWrong, fatigueGain: 0 };
  }
  return {
    hpLoss: pattern.damageOnHit,
    durabilityLoss: pattern.durabilityWrong,
    fatigueGain: 0,
  };
}

/** Bonus damage for a high-quality kill strike. */
export function killBonus(quality: 'perfect' | 'good'): number {
  return quality === 'perfect' ? 1.5 : 1.0;
}

/** Computes gold reward for a victory from the enemy's range, biased by kill quality. */
export function goldFor(enemy: Enemy, killQuality: 'perfect' | 'good'): number {
  const [lo, hi] = enemy.goldRange;
  const base = lo + Math.random() * (hi - lo);
  const bonus = killQuality === 'perfect' ? 1.15 : 1.0;
  return Math.round(base * bonus);
}

export interface BattleSummary {
  result: 'victory' | 'hero_death' | 'weapon_broke';
  weapon: Weapon;
  hero: Hero;
  enemy: Enemy;
  killQuality: 'perfect' | 'good' | null;
}

export function makeOutcome(summary: BattleSummary): BattleOutcome {
  const weaponSurvived = summary.weapon.durability > 0;
  let goldEarned = 0;
  let repDelta = 0;
  let weaponHallOfFame = false;

  if (summary.result === 'victory') {
    const kq = summary.killQuality ?? 'good';
    goldEarned = goldFor(summary.enemy, kq);
    repDelta = REP_VICTORY;
    // Increment battles-won on weapon caller's side; mark hall-of-fame eligibility.
    if (summary.weapon.battlesWon + 1 >= 3) weaponHallOfFame = true;
  } else if (summary.result === 'hero_death') {
    repDelta = REP_DEATH;
  } else if (summary.result === 'weapon_broke') {
    repDelta = REP_WEAPON_BROKE;
  }

  if (weaponHallOfFame) repDelta += REP_HALL_OF_FAME;

  return {
    result: summary.result,
    heroId: summary.hero.id,
    weapon: summary.weapon,
    weaponSurvived,
    goldEarned,
    reputationDelta: repDelta,
    weaponHallOfFameEligible: weaponHallOfFame,
    killQuality: summary.killQuality,
  };
}
